import { type TypeWindowKey, windowRange } from '@/function/calendar/calendar.window';
import { dashboardApi } from '@/store/api/base/api.base';
import { upsertById } from '@/store/api/base/api.cache';
import { dropExternalCalendar } from '@/store/api/calendar/calendar.external.cache';
import type {
    TypeExternalCalendar, TypeExternalCalendarInput, TypeExternalCalendarPatch, TypeExternalOccurrence,
} from '@/types/calendar/types.calendar.external';

export const EVENTS_TAG = 'ExternalEvents' as const;

const KEEP_FOR = 900;

export const externalCalendarApi = dashboardApi.enhanceEndpoints({ addTagTypes: [EVENTS_TAG] }).injectEndpoints({
    endpoints: (build) => ({
        listExternalCalendars: build.query<TypeExternalCalendar[], void>({
            query: () => ({ url: '/calendar/external' }),
            transformResponse: (response: { items: TypeExternalCalendar[] } | null) => response?.items ?? [],
            keepUnusedDataFor: KEEP_FOR,
        }),

        listExternalWindow: build.query<TypeExternalOccurrence[], TypeWindowKey>({
            query: (key) => {
                const range = windowRange(key);
                return { url: '/calendar/external/events', params: { from: range.from.toISOString(), to: range.to.toISOString() } };
            },
            transformResponse: (response: { items: TypeExternalOccurrence[] } | null) => response?.items ?? [],
            providesTags: [EVENTS_TAG],
            keepUnusedDataFor: KEEP_FOR,
        }),

        addExternalCalendar: build.mutation<TypeExternalCalendar, TypeExternalCalendarInput>({
            query: (data) => ({ url: '/calendar/external', method: 'post', data }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(externalCalendarApi.util.updateQueryData('listExternalCalendars', undefined, (draft) => upsertById(draft, data)));
                    dispatch(externalCalendarApi.util.invalidateTags([EVENTS_TAG]));
                } catch {
                }
            },
        }),

        updateExternalCalendar: build.mutation<TypeExternalCalendar, { id: string; patch: TypeExternalCalendarPatch }>({
            query: ({ id, patch }) => ({ url: `/calendar/external/${encodeURIComponent(id)}`, method: 'patch', data: patch }),
            onQueryStarted: async ({ patch }, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(externalCalendarApi.util.updateQueryData('listExternalCalendars', undefined, (draft) => upsertById(draft, data)));
                    if (patch.url) dispatch(externalCalendarApi.util.invalidateTags([EVENTS_TAG]));
                } catch {
                }
            },
        }),

        removeExternalCalendar: build.mutation<unknown, string>({
            query: (id) => ({ url: `/calendar/external/${encodeURIComponent(id)}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dropExternalCalendar(dispatch, id);
                } catch {
                }
            },
        }),

        syncExternalCalendar: build.mutation<TypeExternalCalendar, string>({
            query: (id) => ({ url: `/calendar/external/${encodeURIComponent(id)}/sync`, method: 'post' }),
            onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(externalCalendarApi.util.updateQueryData('listExternalCalendars', undefined, (draft) => upsertById(draft, data)));
                    dispatch(externalCalendarApi.util.invalidateTags([EVENTS_TAG]));
                } catch {
                }
            },
        }),
    }),
});

export const {
    useListExternalCalendarsQuery, useListExternalWindowQuery, useAddExternalCalendarMutation, useUpdateExternalCalendarMutation,
    useRemoveExternalCalendarMutation, useSyncExternalCalendarMutation,
} = externalCalendarApi;

export default externalCalendarApi;
